/**
 * Shared display formatters for the admin app.
 *
 * Keep this file tiny — one-off formatting belongs next to its call site.
 * This module is for formatters used by 2+ components so they stay in sync.
 */
import { formatDistanceToNow } from 'date-fns'

/**
 * Format a number as USD. Used by UserExplorerView's stats strip and any
 * other admin view that needs a consistent dollar display.
 *
 * Defaults to 2 fraction digits for precision; callers that want integer
 * dollars can pass `{ fractionDigits: 0 }`. Uses en-US grouping, which is
 * acceptable for the admin panel (not user-facing).
 */
export function formatCurrency(
  amount: number,
  { fractionDigits = 2 }: { fractionDigits?: 0 | 2 } = {},
): string {
  return `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`
}

/**
 * Format a booking price as either USD (cash) or points (award). The unified
 * formatter eliminates the dozen inline `isAward ? pts : $` branches that
 * were drifting on rounding and label casing.
 */
export function formatPrice(
  amount: number | null | undefined,
  isAward?: boolean | null,
  { fractionDigits = 0 }: { fractionDigits?: 0 | 2 } = {},
): string {
  if (amount == null) return '—'
  if (isAward) return `${Math.round(amount).toLocaleString('en-US')} pts`
  return formatCurrency(amount, { fractionDigits })
}

/**
 * Render an ISO timestamp as "5 minutes ago"-style relative time. Returns
 * `fallback` for nullish inputs so call sites don't each open-code the
 * `value ? formatDistanceToNow(...) : '—'` pattern.
 */
export function timeAgo(iso: string | null | undefined, fallback = '—'): string {
  if (!iso) return fallback
  return formatDistanceToNow(new Date(iso), { addSuffix: true })
}

/**
 * `snake_case` → `snake case`. Used by dialog detail views that surface
 * database enum values (cabin class, extraction status, etc.) directly.
 */
export function humanizeSnakeCase(value: string | null | undefined): string {
  if (!value) return ''
  return value.replace(/_/g, ' ')
}

/** Alias for readability at cabin-class call sites. */
export function formatCabin(cabin: string | null | undefined): string {
  return humanizeSnakeCase(cabin)
}
